import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer, 
  Grid 
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { ProjectPayment } from '../../types';
import apiService from '../../services/api';

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.9rem;
  padding: 0.35rem 0.75rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  color: var(--text-color);
`;

const AttachmentList = styled.div`
  margin-top: 1rem;
`;

const AttachmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--light-gray);
  border-radius: var(--border-radius);
  margin-bottom: 0.5rem;
  
  .attachment-name {
    font-weight: 500;
  }
  
  .attachment-size {
    font-size: 0.8rem;
    color: var(--dark-gray);
  }
`;

const PaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [payment, setPayment] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (id) {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch payment details
          const paymentResponse = await apiService.getPayment(id);
          if (paymentResponse.success && paymentResponse.data) {
            setPayment(paymentResponse.data);
            
            // Fetch project details if projectId exists
            if (paymentResponse.data.projectId) {
              try {
                const projectResponse = await apiService.getProject(paymentResponse.data.projectId);
                if (projectResponse.success && projectResponse.data) {
                  setProject(projectResponse.data);
                }
              } catch (projectError) {
                console.error('Error fetching project:', projectError);
              }
            }
          } else {
            setError('Payment not found');
          }
        } catch (error: any) {
          console.error('Error fetching payment details:', error);
          setError('Failed to load payment details');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPaymentDetails();
  }, [id]);
  
  const getPaymentTypeVariant = (type: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (type) {
      case 'Advance': return 'info';
      case 'Progress': return 'primary';
      case 'Final': return 'success';
      case 'Retention': return 'warning';
      default: return 'secondary';
    }
  };
  
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p>Loading payment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</p>
        <Button as={Link} to="/accounting">
          Back to Accounting
        </Button>
      </div>
    );
  }

  if (!payment) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p>Payment not found</p>
        <Button as={Link} to="/accounting">
          Back to Accounting
        </Button>
      </div>
    );
  }
  
  return (
    <div>
      <DetailHeader>
        <div>
          <h1>Payment Details</h1>
          <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
            Payment ID: {payment._id || payment.id}
          </p>
        </div>
        <FlexContainer gap="0.5rem">
          <StatusBadge variant={getPaymentTypeVariant(payment.paymentType)}>
            {payment.paymentType}
          </StatusBadge>
          <StatusBadge variant={payment.approved ? 'success' : 'warning'}>
            {payment.approved ? 'Approved' : 'Pending'}
          </StatusBadge>
        </FlexContainer>
      </DetailHeader>

      <Grid columns="2fr 1fr" gap="2rem">
        <div>
          <Card>
            <CardHeader>
              <SectionTitle>Payment Information</SectionTitle>
            </CardHeader>
            <CardBody>
              <DetailItem>
                <DetailLabel>Amount</DetailLabel>
                <DetailValue>
                  <strong style={{ fontSize: '1.5rem', color: 'var(--primary-color)' }}>
                    {formatCurrency(payment.amount, payment.currency)}
                  </strong>
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Description</DetailLabel>
                <DetailValue>{payment.description}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Payment Method</DetailLabel>
                <DetailValue>{payment.paymentMethod}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Payment Date</DetailLabel>
                <DetailValue>{formatDate(new Date(payment.paymentDate))}</DetailValue>
              </DetailItem>

              {payment.reference && (
                <DetailItem>
                  <DetailLabel>Reference</DetailLabel>
                  <DetailValue>{payment.reference}</DetailValue>
                </DetailItem>
              )}

              <DetailItem>
                <DetailLabel>Created By</DetailLabel>
                <DetailValue>{payment.createdBy}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Created At</DetailLabel>
                <DetailValue>{formatDate(new Date(payment.createdAt))}</DetailValue>
              </DetailItem>

              {payment.approved && (
                <>
                  <DetailItem>
                    <DetailLabel>Approved By</DetailLabel>
                    <DetailValue>{payment.approvedBy}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Approved At</DetailLabel>
                    <DetailValue>{formatDate(new Date(payment.approvedAt))}</DetailValue>
                  </DetailItem>
                </>
              )}
            </CardBody>
          </Card>

          {payment.attachments && payment.attachments.length > 0 && (
            <Card style={{ marginTop: '1rem' }}>
              <CardHeader>
                <SectionTitle>Attachments</SectionTitle>
              </CardHeader>
              <CardBody>
                <AttachmentList>
                  {payment.attachments.map((attachment: any, index: number) => (
                    <AttachmentItem key={index}>
                      <div>
                        <div className="attachment-name">{attachment.name}</div>
                        <div className="attachment-size">
                          {(attachment.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <OutlineButton as="a" href={attachment.url} target="_blank">
                        Download
                      </OutlineButton>
                    </AttachmentItem>
                  ))}
                </AttachmentList>
              </CardBody>
            </Card>
          )}
        </div>

        <div>
          {project && (
            <Card>
              <CardHeader>
                <SectionTitle>Project Information</SectionTitle>
              </CardHeader>
              <CardBody>
                <DetailItem>
                  <DetailLabel>Project Name</DetailLabel>
                  <DetailValue>{project.name}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Project ID</DetailLabel>
                  <DetailValue>{project._id || project.id}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Location</DetailLabel>
                  <DetailValue>{project.location}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    <StatusBadge variant="info">{project.status}</StatusBadge>
                  </DetailValue>
                </DetailItem>
              </CardBody>
            </Card>
          )}

          <Card style={{ marginTop: '1rem' }}>
            <CardHeader>
              <SectionTitle>Actions</SectionTitle>
            </CardHeader>
            <CardBody>
              <FlexContainer direction="column" gap="0.5rem">
                <Button as={Link} to={`/accounting/payment/edit/${payment._id || payment.id}`}>
                  Edit Payment
                </Button>
                <OutlineButton as="a" href="#" onClick={(e) => {
                  e.preventDefault();
                  window.print();
                }}>
                  Print Details
                </OutlineButton>
                <OutlineButton as={Link} to="/accounting">
                  Back to Accounting
                </OutlineButton>
              </FlexContainer>
            </CardBody>
          </Card>
        </div>
      </Grid>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default PaymentDetail; 